import { useCallback, useRef } from 'react';

/**
 * Returns a stable, debounced wrapper around `fn`.
 *
 * Each call resets the internal timer; `fn` is only invoked once `delay` ms
 * have elapsed without another call. The timer is stored in a ref so it
 * persists across renders without triggering re-creation.
 *
 * @typeParam T - The callback signature.
 * @param fn - The function to debounce.
 * @param delay - Debounce interval in milliseconds.
 * @returns A memoised function with the same signature as `fn`.
 */
const useDebouncedFn = <T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return useCallback(
    (...args: Parameters<T>) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        fn(...args);
      }, delay);
    },
    [fn, delay]
  );
};

// eslint-disable-next-line import/prefer-default-export
export { useDebouncedFn };
